import { getAuth } from "firebase/auth";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import NextImage from "next/image";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import {
  ActionIcon,
  Box,
  Burger,
  Divider,
  Drawer,
  Flex,
  Group,
  Image,
  Indicator,
  Modal,
  rem,
  RemoveScroll,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import {
  IconBell,
  IconBellFilled,
  IconHome2,
  IconLogout,
  IconLifebuoy,
  IconUserFilled,
  IconSettings,
  IconChevronDown,
  IconChevronRight,
} from "@tabler/icons-react";
import { HEADER_HEIGHT } from "@/app/_constants/ui";
import { useNotificationsContext } from "@/app/_providers/NotificationsProvider/NotificationsProvider";
import { useUserContext } from "@/app/_hooks/useUserContext";
import useCanPerformAction from "@/app/_hooks/useCanPerformAction";
import { useSubscriptionContext } from "@/app/_hooks/subscription/useSubscriptionContext";
import { checkFeatureFlag } from "@/app/_config/featureFlags";
import useSettingsTabs from "../SettingsNav/useSettingsTabs";
import {
  hasNotification,
  orderNotifications,
  unreadNotificationLength,
} from "@/app/_helpers/notifications";
import navItems, {
  getSidebarSettings,
  isActiveRoute,
  isHiddenBySidebarSettings,
  sidebarShortcuts,
} from "../../constants/navItems";
import Logo from "@/app/_media/Logo.svg";
import { useSupportContext } from "@/app/_providers/SupportProvider/SupportProvider";
import TeamMemberAvatar from "@/app/_components/TeamMemberAvatar/TeamMemberAvatar";
import ThemeToggle from "@/app/_components/ThemeToggle/ThemeToggle";
import UserNote from "@/app/_components/UserNote/UserNote";
import useConversations from "@/app/_hooks/messages/useConversations";
import useTotalUnreadCount from "@/app/_hooks/messages/useTotalUnreadCount";
import classes from "./MobileHeader.module.css";
import classes2 from "./Alt.module.css";

interface NavbarLinkProps {
  icon: typeof IconHome2;
  label: string;
  active?: boolean;
  onClick?(): void;
}

const MobileHeader = () => {
  const theme = useMantineTheme();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const user = useUserContext();
  const { notifications } = useNotificationsContext();
  const auth = getAuth();
  const canPerformAction = useCanPerformAction();
  const subscription = useSubscriptionContext();
  // Inventory + Expenses are a Standard+ feature (hidden on Lite).
  const isLite = subscription.level === "lite";
  // Supplies feature (inventory/expenses) is a restricted rollout.
  const canUseSupplies = checkFeatureFlag("MM_SUPPLIES_ENABLED", user.uid);

  // Get current tab and subtab from URL
  const currentTab = searchParams.get("tab");
  const currentSubtab = searchParams.get("subtab");
  const isOnSettings = pathname.includes("/settings");

  // Settings sub-navigation state
  const [openSubTabs, setOpenSubTabs] = useState<Set<string>>(() => {
    const initialSet = new Set<string>();

    // If on settings page, auto-expand settings
    if (isOnSettings) {
      initialSet.add("settings");

      // Auto-expand specific tabs based on current page
      if (currentTab === "payments") {
        initialSet.add("payments");
      }
      if (currentTab === "contracts") {
        initialSet.add("contracts");
      }

      // If no specific tab, default to general
      if (!currentTab) {
        // General doesn't have sub-tabs, so just settings is enough
      }
    }

    return initialSet;
  });

  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const [
    notificationsOpened,
    { close: closeNotifications, toggle: toggleNotifications },
  ] = useDisclosure(false);
  const {
    open: openSupport,
    isOpen: supportOpened,
    unreadCount: supportUnreadCount,
  } = useSupportContext();

  useEffect(() => {
    if (drawerOpened) {
      closeDrawer();
    }
  }, [notificationsOpened]);

  useEffect(() => {
    if (drawerOpened) {
      closeDrawer();
    }
  }, [supportOpened]);

  // Settings tabs shared with the desktop SettingsNav (single source of truth).
  const settingsTabs = useSettingsTabs();
  const { conversations } = useConversations();
  const totalUnread = useTotalUnreadCount(conversations);
  const messagingEnabled = user.settings.enableClientMessaging;

  const links = navItems
    .filter(
      (link) =>
        link.path &&
        canPerformAction(link.actionName) &&
        !(
          link.label === "Team" && user.uid === "qikXWTAAxwQuqFW5XPuZMUR9DEG2"
        ) &&
        // Always hide regular Settings link (we always show expandable version instead)
        !(link.label === "Settings") &&
        // Hide Messages when messaging is disabled
        !(link.label === "Messages" && !messagingEnabled) &&
        // Inventory + Expenses are Standard+ (hidden on Lite)
        !(
          isLite &&
          (link.label === "Inventory" || link.label === "Expenses")
        ) &&
        // Supplies feature is a restricted rollout (admin + TMT only)
        (canUseSupplies ||
          (link.label !== "Inventory" && link.label !== "Expenses")) &&
        // Modules hidden for this tenant (see TENANT_SIDEBAR)
        !isHiddenBySidebarSettings(link.label, getSidebarSettings(user.uid))
    )
    .map((item) => {
      const active = item.path && isActiveRoute(item.path, pathname);
      return (
        <Link
          className={classes2.link}
          data-active={active || undefined}
          href={item.path || "/jobs"}
          key={item.label}
          onClick={toggleDrawer}
        >
          <Group gap="sm" justify="space-between" w="100%">
            <Group gap="sm">
              <item.icon
                style={{ width: rem(24), height: rem(24) }}
                stroke={1.5}
              />
              <Text>{item.label}</Text>
            </Group>
            {item.label === "Messages" && totalUnread > 0 && (
              <Indicator color="red" processing size={10} offset={0}>
                <span />
              </Indicator>
            )}
          </Group>
        </Link>
      );
    });

  // Settings pages pinned to the sidebar for this tenant. Support opens the
  // drawer instead of routing.
  const shortcutLinks = sidebarShortcuts
    .filter(
      (shortcut) => getSidebarSettings(user.uid)?.[shortcut.key] === true
    )
    .filter((shortcut) => canPerformAction(shortcut.actionName))
    .map((shortcut) =>
      shortcut.path ? (
        <Link
          className={classes2.link}
          href={shortcut.path}
          key={shortcut.label}
          onClick={toggleDrawer}
        >
          <Group gap="sm">
            <shortcut.icon
              style={{ width: rem(24), height: rem(24) }}
              stroke={1.5}
            />
            <Text>{shortcut.label}</Text>
          </Group>
        </Link>
      ) : (
        <UnstyledButton
          className={classes2.link}
          key={shortcut.label}
          onClick={() => {
            toggleDrawer();
            openSupport();
          }}
        >
          <Group gap="sm">
            <shortcut.icon
              style={{ width: rem(24), height: rem(24) }}
              stroke={1.5}
            />
            <Text>{shortcut.label}</Text>
          </Group>
        </UnstyledButton>
      )
    );

  return (
    <Box hiddenFrom="sm" component="header">
      <Flex
        justify="space-between"
        align="center"
        p="sm"
        bg={theme.colors.primary[5]}
        h={HEADER_HEIGHT}
      >
        <Link href={`/`}>
          <TeamMemberAvatar teamMember={user.details} size="sm" />
        </Link>

        <Burger
          size="sm"
          color={theme.white}
          className={classes.headerIcon}
          onClick={toggleDrawer}
        />
      </Flex>

      <Drawer.Root
        opened={drawerOpened}
        onClose={closeDrawer}
        size="100%"
        hiddenFrom="sm"
        zIndex={1000000}
        transitionProps={{
          transition: "slide-right",
          duration: 300,
          timingFunction: "ease",
        }}
        closeOnClickOutside
      >
        <Drawer.Content>
          <Flex
            align="center"
            gap="sm"
            w={"100%"}
            justify="space-between"
            bg={theme.colors.primary[5]}
            h={HEADER_HEIGHT}
            px="sm"
          >
            <Link href={`/`} onClick={toggleDrawer}>
              <Image
                component={NextImage}
                src={Logo}
                alt="MountMonitor"
                height={36}
                w="auto"
                ml={10}
              />
            </Link>
            {/* <Link href={`/jobs`} onClick={toggleDrawer}>
                <Text c={theme.white} fw={700}>
                  MountMonitor
                </Text>
              </Link> */}
            <Burger
              size="sm"
              color={theme.white}
              className={classes.headerIcon}
              opened={drawerOpened}
              onClick={toggleDrawer}
            />
          </Flex>

          <Drawer.Body p="md">
            <Flex
              component="nav"
              direction="column"
              h="100%"
              justify="space-between"
            >
              <RemoveScroll>
                <ScrollArea h="calc(100dvh - 132px)">
                  <Flex direction="column">
                    <UnstyledButton
                      className={classes2.link}
                      w="100%"
                      onClick={() => toggleNotifications()}
                    >
                      <Group gap="sm">
                        <Indicator
                          offset={5}
                          color="red"
                          processing={hasNotification(notifications)}
                          disabled={!hasNotification(notifications)}
                          label={
                            <Text fw={700} size="10">
                              {unreadNotificationLength(notifications)}
                            </Text>
                          }
                          size={16}
                          position="top-end"
                        >
                          {hasNotification(notifications) ? (
                            <IconBellFilled
                              style={{ width: rem(24), height: rem(24) }}
                              stroke={1.5}
                            />
                          ) : (
                            <IconBell
                              style={{ width: rem(24), height: rem(24) }}
                              stroke={1.3}
                            />
                          )}
                        </Indicator>
                        <Text>Notifications</Text>
                      </Group>
                    </UnstyledButton>

                    {/* Show normal navigation with settings expansion if on settings page */}
                    {links}
                    {shortcutLinks}

                    {/* Always show settings as accordion dropdown */}
                    <Box>
                      <UnstyledButton
                        className={classes2.link}
                        w="100%"
                        onClick={() => {
                          setOpenSubTabs((prev) => {
                            const newSet = new Set(prev);
                            if (newSet.has("settings")) {
                              newSet.delete("settings");
                            } else {
                              newSet.add("settings");
                            }
                            return newSet;
                          });
                        }}
                      >
                        <Group gap="sm" justify="space-between" w="100%">
                          <Group gap="sm">
                            <IconSettings
                              style={{ width: rem(24), height: rem(24) }}
                              stroke={1.5}
                            />
                            <Text>Settings</Text>
                          </Group>
                          {openSubTabs.has("settings") ? (
                            <IconChevronDown size={16} stroke={1.5} />
                          ) : (
                            <IconChevronRight size={16} stroke={1.5} />
                          )}
                        </Group>
                      </UnstyledButton>

                      {openSubTabs.has("settings") && (
                        <Stack gap="xs" pl="lg" mt="xs">
                          {settingsTabs.map((tab) => {
                            const Icon = tab.icon;
                            // Only highlight if this tab has no children AND is the current tab AND no subtab is selected
                            const isActive =
                              !tab.subTabs &&
                              pathname.includes(`/settings`) &&
                              (currentTab === tab.key ||
                                (tab.key === "general" && !currentTab)) &&
                              !currentSubtab; // Don't highlight parent when subtab is active

                            return (
                              <Box key={tab.key}>
                                <UnstyledButton
                                  className={classes2.link}
                                  data-active={isActive || undefined}
                                  w="100%"
                                  onClick={() => {
                                    if (tab.subTabs) {
                                      // For tabs with sub-tabs, only toggle expansion
                                      setOpenSubTabs((prev) => {
                                        const newSet = new Set(prev);
                                        newSet.add("settings"); // Keep settings open
                                        if (newSet.has(tab.key)) {
                                          newSet.delete(tab.key);
                                        } else {
                                          newSet.add(tab.key);
                                        }
                                        return newSet;
                                      });
                                    } else {
                                      // For tabs without sub-tabs, navigate and close drawer
                                      router.push(tab.path);
                                      toggleDrawer();
                                    }
                                  }}
                                >
                                  <Group
                                    gap="sm"
                                    justify="space-between"
                                    w="100%"
                                  >
                                    <Group gap="sm">
                                      <Icon
                                        style={{
                                          width: rem(20),
                                          height: rem(20),
                                        }}
                                        stroke={1.5}
                                      />
                                      <Text size="sm">{tab.label}</Text>
                                    </Group>
                                    {tab.subTabs &&
                                      (openSubTabs.has(tab.key) ? (
                                        <IconChevronDown
                                          size={14}
                                          stroke={1.5}
                                        />
                                      ) : (
                                        <IconChevronRight
                                          size={14}
                                          stroke={1.5}
                                        />
                                      ))}
                                  </Group>
                                </UnstyledButton>

                                {tab.subTabs && openSubTabs.has(tab.key) && (
                                  <Stack gap="xs" pl="lg" mt="xs">
                                    {tab.subTabs.map((subTab) => {
                                      const SubIcon = subTab.icon;
                                      const isSubActive =
                                        currentSubtab === subTab.key ||
                                        (!currentSubtab &&
                                          subTab.key === "transactions" &&
                                          currentTab === "payments");

                                      return (
                                        <UnstyledButton
                                          key={subTab.key}
                                          className={classes2.link}
                                          data-active={isSubActive || undefined}
                                          w="100%"
                                          onClick={() => {
                                            router.push(subTab.path);
                                            toggleDrawer();
                                          }}
                                        >
                                          <Group gap="sm" w="100%">
                                            <SubIcon
                                              style={{
                                                width: rem(16),
                                                height: rem(16),
                                              }}
                                              stroke={1.5}
                                            />
                                            <Text size="xs">
                                              {subTab.label}
                                            </Text>
                                          </Group>
                                        </UnstyledButton>
                                      );
                                    })}
                                  </Stack>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </Flex>
                </ScrollArea>
              </RemoveScroll>
              <Box>
                <Divider />
                <Flex mt="md" align="center" justify="space-between">
                  <Link href={`/settings`} onClick={toggleDrawer}>
                    {user.settings.logoSrc ? (
                      <Image
                        priority
                        component={NextImage}
                        src={user.settings.logoSrc}
                        alt={user.settings.name}
                        width={32}
                        height={32}
                        radius="50%"
                        style={{ cursor: "pointer" }}
                      />
                    ) : (
                      <Flex
                        w={32}
                        h={32}
                        bg={theme.colors.primary[9]}
                        align="center"
                        justify="center"
                        style={{ borderRadius: "50%" }}
                      >
                        <IconUserFilled
                          style={{
                            width: rem(24),
                            height: rem(24),
                            color: "white",
                          }}
                        />
                      </Flex>
                    )}
                  </Link>
                  <Group gap="xs" wrap="nowrap">
                    <Tooltip label="Support">
                      <Indicator
                        color="red"
                        processing
                        size={8}
                        offset={4}
                        disabled={supportUnreadCount === 0}
                      >
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="lg"
                          aria-label="Support"
                          onClick={() => openSupport()}
                        >
                          <IconLifebuoy size={22} stroke={1.5} />
                        </ActionIcon>
                      </Indicator>
                    </Tooltip>
                    <Tooltip label="Logout">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="lg"
                        aria-label="Logout"
                        onClick={() => auth.signOut()}
                      >
                        <IconLogout size={22} stroke={1.5} />
                      </ActionIcon>
                    </Tooltip>
                    <ThemeToggle />
                  </Group>
                </Flex>
              </Box>
            </Flex>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>

      <Modal
        opened={notificationsOpened}
        onClose={closeNotifications}
        centered
        title={
          <Text size="xl" fw={500}>
            Notifications
          </Text>
        }
      >
        <ScrollArea.Autosize h={500} mih={300}>
          <Stack gap="xs">
            {notifications.length > 0 ? (
              orderNotifications(notifications).map((notification, index) => (
                <UserNote
                  closeNotification={() => closeNotifications()}
                  notification={notification}
                  key={notification.id}
                />
              ))
            ) : (
              <Stack h={300} align="center" justify="center">
                <Text fw={500}>No notifications</Text>
              </Stack>
            )}
          </Stack>
        </ScrollArea.Autosize>
      </Modal>
    </Box>
  );
};

export default MobileHeader;
